/**
 * Ejemplos de preguntas de negocio vs lo que el builder puede expresar hoy.
 * Agregar nuevos objetos a CHART_BUILDER_QUESTION_EXAMPLES para ampliar la lista.
 *
 * Cada ejemplo puede incluir `wizard`: preset cargable en ChartBuilderDialog.
 * Preferir formato dataset: rowHints / valueHints (+ visualization).
 * Los hints (roles / tableHints) se resuelven contra el modelo activo.
 *
 * status:
 *   - supported: el builder puede aproximarlo hoy (con SQL manual menor)
 *   - partial: partes posibles; ver gaps
 *   - planned: requiere trabajo de producto (ver gaps)
 */

export const CHART_BUILDER_QUESTION_EXAMPLES = [
  {
    id: 'top-stores-by-cameras',
    title: 'Top 10 tiendas con más cámaras',
    businessQuestion:
      'Quiero ver las 10 tiendas que tienen más cámaras. Rankear los top 10 assets sin parent_id que tienen más assets hijos.',
    physicalModel:
      'Tabla jerárquica (ej. asset): raíz = parent_id IS NULL (tienda); hijos = filas con parent_id = id del padre (cámara). Medida = COUNT(hijos). Orden = por conteo DESC. Límite = 10.',
    businessTerms: ['tienda', 'cámara'],
    suggestedChartType: 'bar',
    status: 'planned',
    gaps: ['Self-join padre/hijo', 'Roles parent/child en joins'],
    wizard: {
      kind: 'dataset',
      title: 'Top 10 tiendas con más cámaras',
      tableHints: ['asset', 'device', 'camera', 'tienda'],
      rowLimit: 10,
      orderByKind: 'measure',
      orderDirection: 'desc',
      rowHints: [{ roles: ['name', 'nombre', 'label'], aggregation: 'none' }],
      valueHints: [{ roles: ['id', 'asset_id'], aggregation: 'count', preferPk: true }],
      visualization: { chartType: 'bar' },
      filterHints: [
        {
          layer: 'universe',
          roles: ['parent_id', 'parentid', 'parent'],
          operator: 'isNull',
        },
      ],
      notes:
        'Intención: agrupar tiendas (parent_id IS NULL) y contar hijos. Sin self-join el builder solo puede aproximar (filtro + COUNT sobre la misma tabla).',
    },
  },
  {
    id: 'cameras-per-store',
    title: 'Cámaras por tienda',
    businessQuestion:
      'Una tabla donde se muestre el total de assets hijos por cada asset sin parent_id (tienda).',
    physicalModel:
      'JOIN asset padre ON padre.parent_id IS NULL con asset hijo ON hijo.parent_id = padre.id. SELECT padre.name, COUNT(hijo.id) GROUP BY padre.name.',
    businessTerms: ['tienda', 'cámara'],
    suggestedChartType: 'table',
    status: 'planned',
    gaps: ['Self-join', 'Nombre del padre + conteo de hijos'],
    wizard: {
      kind: 'dataset',
      title: 'Cámaras por tienda',
      tableHints: ['asset', 'device', 'camera', 'tienda'],
      rowLimit: 500,
      orderByKind: 'column',
      orderDirection: 'desc',
      rowHints: [{ roles: ['parent_id', 'parentid', 'parent'], aggregation: 'none' }],
      valueHints: [{ roles: ['id', 'asset_id'], aggregation: 'count', preferPk: true }],
      visualization: { chartType: 'table' },
      orderColumnHint: { roles: ['id', 'asset_id'] },
      filterHints: [
        {
          layer: 'universe',
          roles: ['parent_id', 'parentid', 'parent'],
          operator: 'isNotNull',
        },
      ],
      notes:
        'Intención: total de hijos por parent_id. Idealmente dimensión = nombre del padre (requiere self-join). Revisa columnas y agregación COUNT.',
    },
  },
  {
    id: 'most-data-last-month',
    title: 'Qué tienda/cámara recibió más datos en el último mes',
    businessQuestion:
      'El nombre del padre que acumula más lecturas entre sus sensores y los de sus assets hijos, en el último mes.',
    physicalModel:
      'readings → asset hijo → asset padre (self-join). SUM(valor) o COUNT(*). Filtro timestamp >= inicio del mes anterior. ORDER BY medida DESC.',
    businessTerms: ['tienda', 'cámara', 'dato', 'sensor'],
    suggestedChartType: 'bar',
    status: 'planned',
    gaps: ['Self-join en cadena', 'Filtros de fecha relativos', 'Ranking por medida'],
    wizard: {
      kind: 'dataset',
      title: 'Más datos en el último mes',
      tableHints: ['reading', 'sensor', 'data', 'telemetry', 'metric'],
      rowLimit: 10,
      orderByKind: 'measure',
      orderDirection: 'desc',
      rowHints: [
        {
          tableHints: ['asset', 'device', 'sensor'],
          roles: ['name', 'nombre'],
        },
      ],
      valueHints: [
        {
          tableHints: ['reading', 'sensor', 'data', 'telemetry'],
          roles: ['id', 'value'],
          aggregation: 'count',
        },
      ],
      visualization: { chartType: 'bar' },
      filterHints: [
        {
          layer: 'universe',
          tableHints: ['reading', 'sensor', 'data', 'telemetry'],
          roles: ['timestamp', 'ts', 'time', 'created', 'fecha'],
          operator: 'gte',
          value: '',
          logicalTypes: ['datetime', 'date'],
        },
      ],
      notes:
        'Fecha relativa “último mes” aún no está en el builder: pon una fecha de inicio manual en el filtro. Self-join tienda←sensor pendiente.',
    },
  },
  {
    id: 'last-readings-ingreso-sensors',
    title: 'Últimos 10 datos de sensores "ingreso" + selector dinámico',
    businessQuestion:
      'Ver los últimos 10 datos de sensores con nombre similar a "ingreso", con un filtro dinámico que liste todos los sensores que cumplen ese nombre.',
    physicalModel:
      'sensor_dataset_items (time, sensor_id, …) JOIN vsaas_sensors (name) ON sensor_id. ORDER BY time DESC LIMIT 10. Universo: name Contiene "ingreso". Selección @Sensor sobre name.',
    businessTerms: ['sensor', 'ingreso', 'dato'],
    suggestedChartType: 'table',
    status: 'supported',
    gaps: [],
    wizard: {
      kind: 'dataset',
      title: 'Últimos 10 datos — sensores ingreso',
      tableHints: ['sensor_dataset', 'dataset_items', 'reading', 'telemetry'],
      rowLimit: 10,
      orderByKind: 'column',
      orderDirection: 'desc',
      rowHints: [
        {
          tableHints: ['sensor_dataset', 'dataset_items', 'reading', 'telemetry'],
          roles: ['time', 'timestamp', 'ts', 'created_at'],
          logicalTypes: ['datetime', 'date'],
          aggregation: 'none',
        },
        {
          tableHints: ['vsaas_sensors', 'sensor'],
          roles: ['name', 'nombre', 'sensor_name', 'label'],
          aggregation: 'none',
        },
        {
          tableHints: ['sensor_dataset', 'dataset_items', 'reading', 'telemetry'],
          roles: ['value', 'data', 'payload', 'json', 'item'],
          preferJson: true,
          aggregation: 'none',
        },
      ],
      valueHints: [],
      visualization: { chartType: 'table' },
      orderColumnHint: {
        tableHints: ['sensor_dataset', 'dataset_items', 'reading'],
        roles: ['time', 'timestamp', 'ts'],
      },
      filterHints: [
        {
          layer: 'universe',
          tableHints: ['vsaas_sensors', 'sensor'],
          roles: ['name', 'nombre', 'sensor_name', 'label'],
          operator: 'contains',
          value: 'ingreso',
        },
        {
          layer: 'selection',
          tableHints: ['vsaas_sensors', 'sensor'],
          roles: ['name', 'nombre', 'sensor_name', 'label'],
          operator: 'eq',
          paramName: 'Sensor',
          previewValue: '',
        },
      ],
      notes:
        'Con FK sensor_dataset_items.sensor_id → vsaas_sensors confirmada, el join lo resuelve el builder. Revisa columnas time / name / valor al cargar.',
    },
  },
  {
    id: 'last-raw-reading-one-sensor',
    title: 'Último dato crudo de un sensor (filtro dinámico por nombre)',
    businessQuestion:
      'Ver el último dato crudo de un sensor específico, eligiendo el sensor por nombre desde un filtro dinámico en el gráfico.',
    physicalModel:
      'Tabla detalle: ORDER BY timestamp DESC, LIMIT 1. Filtro dinámico @SensorName sobre sensor.name.',
    businessTerms: ['sensor', 'dato crudo', 'último registro'],
    suggestedChartType: 'table',
    status: 'partial',
    gaps: [
      'Sprint A OK: ORDER BY + LIMIT 1 + selección dinámica con optionsQuery',
      'Pendiente: vista amigable de JSON crudo completo',
    ],
    wizard: {
      kind: 'dataset',
      title: 'Último dato crudo — un sensor',
      tableHints: ['sensor', 'reading', 'asset', 'device', 'telemetry'],
      rowLimit: 1,
      orderByKind: 'column',
      orderDirection: 'desc',
      rowHints: [
        { roles: ['timestamp', 'ts', 'time', 'created_at', 'fecha'], logicalTypes: ['datetime', 'date'] },
        { roles: ['name', 'nombre', 'sensor_name', 'label'] },
        { roles: ['value', 'data', 'payload', 'json', 'raw'], preferJson: true },
      ],
      valueHints: [],
      visualization: { chartType: 'table' },
      orderColumnHint: { roles: ['timestamp', 'ts', 'time', 'created_at'] },
      filterHints: [
        {
          layer: 'selection',
          roles: ['name', 'nombre', 'sensor_name', 'label'],
          operator: 'eq',
          paramName: 'Sensor',
          previewValue: '',
        },
      ],
      notes: 'Elige un valor de prueba en el filtro de selección para la vista previa; en el dashboard verás el dropdown.',
    },
  },
  {
    id: 'hourly-donut-stock-json-detections',
    title: 'Stock por hora (promedio) — detections JSON en tiendas bakery',
    businessQuestion:
      'Saber el stock por hora promedio de donuts en la tienda o tiendas X. Gráfico con el promedio por hora del total de elementos dentro del campo json.detections, de sensores que pertenecen a las tiendas seleccionadas, filtrando tiendas cuyo nombre sea similar a "stock fresh bakery".',
    physicalModel:
      'readings → sensor → tienda. Dimensión: bucket hora. Medida: AVG(array length de json.detections). Universo: tienda Contiene "stock fresh bakery". Selección: @Tienda.',
    businessTerms: ['tienda', 'sensor', 'stock', 'donuts', 'detections', 'json'],
    suggestedChartType: 'line',
    status: 'planned',
    gaps: [
      'arrayLength JSON',
      'Self-join + cadena',
      'Date bucket hour',
      'Multi-select tiendas',
    ],
    wizard: {
      kind: 'dataset',
      title: 'Stock por hora — fresh bakery',
      tableHints: ['reading', 'sensor', 'telemetry', 'data', 'metric'],
      rowLimit: 500,
      orderByKind: 'dimension',
      orderDirection: 'asc',
      rowHints: [
        {
          roles: ['timestamp', 'ts', 'time', 'created_at'],
          logicalTypes: ['datetime', 'date'],
          dateBucket: 'day',
        },
      ],
      valueHints: [
        {
          roles: ['data', 'payload', 'json', 'value', 'detections'],
          preferJson: true,
          aggregation: 'avg',
          jsonPath: 'detections',
        },
      ],
      visualization: { chartType: 'line' },
      filterHints: [
        {
          layer: 'universe',
          tableHints: ['asset', 'device', 'store', 'tienda', 'sensor'],
          roles: ['name', 'nombre', 'label'],
          operator: 'contains',
          value: 'stock fresh bakery',
        },
        {
          layer: 'selection',
          tableHints: ['asset', 'device', 'store', 'tienda', 'sensor'],
          roles: ['name', 'nombre', 'label'],
          operator: 'eq',
          paramName: 'Tienda',
          previewValue: '',
        },
      ],
      notes:
        'Bucket “hora” y AVG(array length de detections) aún no están; se carga day + AVG del path JSON como aproximación para revisar el flujo. Ajusta joins/columnas a tu modelo.',
    },
  },
];

/** Etiquetas legibles para status */
export const EXAMPLE_STATUS_LABELS = {
  supported: { label: 'Soportado', color: 'success' },
  partial: { label: 'Parcial', color: 'warning' },
  planned: { label: 'Pendiente', color: 'default' },
  custom: { label: 'Guardado', color: 'primary' },
};
